import React, { useEffect, useRef } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Button } from "@deere/ux.uxframe-react";
import { ArrowBack, FileDownload } from "@mui/icons-material";
import ExcelJS from "exceljs";

const VIRTUALIZATION_THRESHOLD = 100;
const ROW_HEIGHT = 40;
const OVERSCAN_COUNT = 10;

const COLUMNS = [
  { key: "acctId",           label: "Dealer" },
  { key: "acctNm",           label: "Dealer Name" },
  { key: "businessPhone",    label: "Business Phone" },
  { key: "regionCd",         label: "Region" },
  {
    key: "dealerLocation",
    label: "Dealer Location",
    render: (c) =>
      [c.fstrAddr1, c.fstrAddr2, c.pcity, c.pstAbbr, c.ppstlCd, c.pcountry]
        .filter(Boolean)
        .join(", ") || "—",
  },
  { key: "claimTypeCd",      label: "Claim Type" },
  { key: "mstrWrntyTypNm",   label: "Warranty Type" },
  { key: "correctPinNum13",  label: "PIN" },
  { key: "wrntyClmNum",      label: "Warranty Claim",
    render: (c) => c.wrntyClmNum
      ? <Link
          to={`/claim-search/claimNumber/${c.wrntyClmNum}`}
          style={{ color: "#367C2B", textDecoration: "underline", fontWeight: 500 }}
        >
          {c.wrntyClmNum}
        </Link>
      : "",
  },
  { key: "failureDt",        label: "Failure Date" },
  { key: "repairCmplDt",     label: "Repair Date" },
  { key: "primePartNum",     label: "Failure Part" },
  { key: "partNameDsc",      label: "Failure Part Description" },
  { key: "customerNm",       label: "Customer Name" },
  { key: "claimReceivedDt",  label: "Claim Received Date" },
  { key: "creditMemoNum",    label: "Credit Memo Number" },
  { key: "lastScrubbedDt",   label: "Credit Memo Date" },
  { key: "locClaimCrncyCd",  label: "Currency" },
  { key: "locTotClaimAmt",   label: "Credit Memo Total" },
];

const sanitizeExcelValue = (value) => {
  if (typeof value === "string" && /^[=+\-@]/.test(value)) {
    return `'${value}`;
  }

  return value;
};

const getExportValue = (claim, column) => {
  if (column.key === "dealerLocation") {
    return sanitizeExcelValue([claim.fstrAddr1, claim.fstrAddr2, claim.pcity, claim.pstAbbr, claim.ppstlCd, claim.pcountry]
      .filter(Boolean)
      .join(", "));
  }

  return sanitizeExcelValue(claim[column.key] ?? "");
};

const DealerClaims = () => {
  const history = useHistory();
  const { state } = useLocation();
  const { row, claimsResults = [] } = state || {};
  const [scrollMetrics, setScrollMetrics] = React.useState({ scrollTop: 0, containerHeight: 0 });

  const tableScrollRef = useRef(null);
  const mirrorScrollRef = useRef(null);
  const mirrorInnerRef = useRef(null);

  useEffect(() => {
    const tableEl = tableScrollRef.current;
    const mirrorEl = mirrorScrollRef.current;
    const mirrorInner = mirrorInnerRef.current;

    const updateLayout = () => {
      if (!tableEl) {
        return;
      }

      if (mirrorInner) {
        mirrorInner.style.width = tableEl.scrollWidth + "px";
      }

      setScrollMetrics((current) => {
        const nextMetrics = {
          scrollTop: tableEl.scrollTop,
          containerHeight: tableEl.clientHeight,
        };

        return current.scrollTop === nextMetrics.scrollTop && current.containerHeight === nextMetrics.containerHeight
          ? current
          : nextMetrics;
      });
    };

    updateLayout();

    const onTableScroll = () => {
      if (mirrorEl) {
        mirrorEl.scrollLeft = tableEl.scrollLeft;
      }

      setScrollMetrics((current) => {
        const nextMetrics = {
          scrollTop: tableEl?.scrollTop ?? 0,
          containerHeight: tableEl?.clientHeight ?? 0,
        };

        return current.scrollTop === nextMetrics.scrollTop && current.containerHeight === nextMetrics.containerHeight
          ? current
          : nextMetrics;
      });
    };
    const onMirrorScroll = () => { if (tableEl) tableEl.scrollLeft = mirrorEl.scrollLeft; };

    tableEl?.addEventListener("scroll", onTableScroll);
    mirrorEl?.addEventListener("scroll", onMirrorScroll);
    window.addEventListener("resize", updateLayout);

    return () => {
      tableEl?.removeEventListener("scroll", onTableScroll);
      mirrorEl?.removeEventListener("scroll", onMirrorScroll);
      window.removeEventListener("resize", updateLayout);
    };
  }, [claimsResults]);

  const shouldVirtualize = claimsResults.length > VIRTUALIZATION_THRESHOLD && scrollMetrics.containerHeight > 0;
  const startIndex = shouldVirtualize
    ? Math.max(0, Math.floor(scrollMetrics.scrollTop / ROW_HEIGHT) - OVERSCAN_COUNT)
    : 0;
  const endIndex = shouldVirtualize
    ? Math.min(
        claimsResults.length,
        Math.ceil((scrollMetrics.scrollTop + scrollMetrics.containerHeight) / ROW_HEIGHT) + OVERSCAN_COUNT
      )
    : claimsResults.length;
  const visibleRows = shouldVirtualize ? claimsResults.slice(startIndex, endIndex) : claimsResults;
  const topSpacerHeight = shouldVirtualize ? startIndex * ROW_HEIGHT : 0;
  const bottomSpacerHeight = shouldVirtualize ? (claimsResults.length - endIndex) * ROW_HEIGHT : 0;

  const handleExportClaims = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Claim Details");

    worksheet.columns = COLUMNS.map((column) => ({ header: column.label, key: column.key }));
    claimsResults.forEach((claim) => {
      worksheet.addRow(COLUMNS.reduce((exportRow, column) => {
        exportRow[column.key] = getExportValue(claim, column);
        return exportRow;
      }, {}));
    });
    worksheet.getRow(1).font = { bold: true };
    worksheet.columns.forEach((column) => {
      column.width = Math.min(Math.max(column.header.length + 2, 15), 40);
    });

    const file = new Blob([await workbook.xlsx.writeBuffer()], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const downloadUrl = window.URL.createObjectURL(file);
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadUrl;
    downloadLink.download = "mpr_claim_details.xlsx";
    downloadLink.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px", position: "sticky", top: 0, zIndex: 10, backgroundColor: "#fff", paddingBottom: "8px" }}>
        <Button
          variant="tertiary"
          onClick={() => history.goBack()}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0", width: "fit-content" }}
        >
          <ArrowBack fontSize="small" />
          Back
        </Button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h4 style={{ margin: 0, fontWeight: 600 }}>Claims history</h4>
          {claimsResults.length > 0 && (
            <Button
              variant="primary"
              onClick={handleExportClaims}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FileDownload fontSize="small" />
              Download to Excel
            </Button>
          )}
        </div>
        {row?.pin17 && (
          <span style={{ color: "#555", fontSize: "14px" }}>
            PIN: <strong>{row.pin17}</strong>
          </span>
        )}
      </div>

      {claimsResults.length === 0 ? (
        <p style={{ color: "#888" }}>No claims found.</p>
      ) : (
        <>
          <p style={{ marginBottom: "12px", fontSize: "14px", color: "#555" }}>
            {claimsResults.length} claim{claimsResults.length !== 1 ? "s" : ""} found
          </p>
          <div ref={tableScrollRef} className="claims-table-scroll" style={{ border: "1px solid #E0E0E0", borderRadius: "6px", overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 220px)", scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`.claims-table-scroll::-webkit-scrollbar { display: none; }`}</style>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", whiteSpace: "nowrap" }}>
              <thead>
                <tr style={{ backgroundColor: "#F5F5F5", borderBottom: "2px solid #D0D0D0" }}>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#333",
                        borderRight: "1px solid #E0E0E0",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        backgroundColor: "#F5F5F5",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={COLUMNS.length} style={{ height: `${topSpacerHeight}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
                {visibleRows.map((claim, rowOffset) => {
                  const i = startIndex + rowOffset;

                  return (
                  <tr
                    key={i}
                    style={{ borderBottom: "1px solid #E0E0E0", backgroundColor: i % 2 === 0 ? "#fff" : "#FAFAFA" }}
                  >
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        style={{ padding: "9px 14px", color: "#444", borderRight: "1px solid #E0E0E0" }}
                      >
                        {col.render ? col.render(claim) : (claim[col.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {bottomSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={COLUMNS.length} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div
        ref={mirrorScrollRef}
        style={{ position: "sticky", bottom: 0, overflowX: "auto", overflowY: "hidden", height: "16px", backgroundColor: "transparent" }}
      >
        <div ref={mirrorInnerRef} style={{ height: "1px" }} />
      </div>
    </div>
  );
};

export default DealerClaims;
